import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Check, Crown, Users, Compass, ArrowRight, Loader2,
  PenLine, ScanLine, Wand2, FileText, Globe, Zap, Download, UserCog,
  TrendingUp, Award,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  "sparkles": Sparkles, "pen-line": PenLine, "scan-line": ScanLine,
  "wand-2": Wand2, "file-text": FileText, "globe": Globe, "zap": Zap,
  "download": Download, "user-cog": UserCog, "trending-up": TrendingUp,
  "compass": Compass, "award": Award,
};
const TIER_ICON = { free: Sparkles, pro: Crown, institution: Users, creator: Award };

/**
 * Reusable upgrade-plan block. Loads plan catalog + current tier and renders 4 cards.
 *
 * Props:
 *  - variant: "full" (default) | "compact" (single-row recommendation chip)
 *  - source: short string ("profile" | "wallet" | "resume") for analytics
 *  - id: anchor id (used by deep links like /profile#plans)
 */
export default function PlanUpgradeSection({ variant = "full", source = "profile", id = "plans" }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [selecting, setSelecting] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [pls, mine] = await Promise.all([
          api.get("/plans"),
          api.get("/plans/me"),
        ]);
        setPlans(pls.data);
        setMe(mine.data);
      } catch { /* silent: section just won't render */ }
    })();
  }, []);

  const select = async (tier) => {
    if (tier === me?.tier) return;
    setSelecting(tier);
    try {
      // For paid tiers we route to Wallet for the mocked recharge flow OR to a sales contact
      const plan = plans.find((p) => p.tier === tier);
      if (plan?.cta_kind === "sales") {
        toast.info("We'll be in touch", { description: "TeeBee Sales will reach out within one business day." });
        // Stamp tier optimistically so the UI updates (mocked)
        await api.post(`/plans/select/${tier}`);
        const { data } = await api.get("/plans/me");
        setMe(data);
        return;
      }
      // Free upgrade or other tier toggle (mocked — no payment in 10b-B)
      await api.post(`/plans/select/${tier}`);
      const { data } = await api.get("/plans/me");
      setMe(data);
      toast.success(`Welcome to ${plan?.name || tier}`, {
        description: "Plan switched in demo mode. Real payments arrive soon.",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch plan");
    } finally {
      setSelecting(null);
    }
  };

  if (!plans || !me) return null;

  if (variant === "compact") {
    const next = plans.find((p) => p.tier === me.upgrade_suggested);
    if (!next) return null;
    const Icon = TIER_ICON[next.tier] || Crown;
    return (
      <div
        id={id}
        data-source={source}
        className="tb-card-feature relative overflow-hidden mt-5"
        data-testid={`plan-compact-${source}`}
      >
        <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="h-10 w-10 rounded-xl tb-ai-orb grid place-items-center shrink-0">
            <Icon className="h-5 w-5 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">{next.badge || "Upgrade"}</p>
            <p className="font-display text-lg sm:text-xl font-medium tracking-tight">Unlock {next.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{next.tagline}</p>
          </div>
          <Button onClick={() => document.getElementById("plans-full")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full" data-testid="plan-compact-cta">
            See plans <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <section id={id} data-source={source} className="mt-10" data-testid={`plan-section-${source}`}>
      <div className="mb-5">
        <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Crown className="h-3 w-3" /> Your TeeBee Plan
        </span>
        <h2 className="mt-3 font-display text-2xl sm:text-3xl tracking-tight font-medium leading-tight">
          Unlock your full educator power
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
          You&apos;re on <span className="font-medium text-foreground">{me.name}</span>. Upgrade anytime — your coins and lessons travel with you.
        </p>
      </div>

      {/* Nudges */}
      {me.nudges?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          {me.nudges.map((n) => (
            <div
              key={n.id}
              className={`rounded-2xl p-4 border flex items-start gap-3 ${
                n.severity === "warning"
                  ? "border-amber-300/60 bg-amber-50/70 dark:bg-amber-500/10 dark:border-amber-500/30"
                  : "border-border bg-card/60"
              }`}
              data-testid={`plan-nudge-${n.id}`}
            >
              <span className="tb-chip-butter h-9 w-9 rounded-xl grid place-items-center shrink-0">
                <Sparkles className="h-4 w-4" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{n.title}</p>
                <p className="text-[12px] text-muted-foreground mt-0.5">{n.body}</p>
              </div>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate(n.cta_path)} data-testid={`plan-nudge-cta-${n.id}`}>
                {n.cta_label}
              </Button>
            </div>
          ))}
        </div>
      )}

      <div id="plans-full" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((p) => {
          const isCurrent = p.tier === me.tier;
          const Icon = TIER_ICON[p.tier] || Sparkles;
          const isRecommended = p.tier === me.upgrade_suggested;
          return (
            <div
              key={p.tier}
              className={`relative rounded-3xl border bg-card p-6 transition-all flex flex-col ${
                isRecommended
                  ? "border-primary/60 shadow-lg shadow-primary/10 -translate-y-0.5"
                  : "border-border hover:border-primary/30"
              }`}
              data-testid={`plan-card-${p.tier}`}
            >
              {p.badge && (
                <span className={`absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${
                  isRecommended ? "bg-primary text-primary-foreground" : "tb-chip-lavender"
                }`}>
                  {p.badge}
                </span>
              )}

              <div className="flex items-center gap-2.5">
                <span className={`h-10 w-10 rounded-2xl grid place-items-center ${p.accent}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-display text-base font-medium tracking-tight">{p.name}</p>
                  <p className="text-[11px] text-muted-foreground line-clamp-1">{p.tagline}</p>
                </div>
              </div>

              <div className="mt-5">
                <p className="font-display text-3xl tracking-tight font-medium">
                  {p.price_inr === 0
                    ? <>Free</>
                    : <>₹{p.price_inr}<span className="text-base text-muted-foreground font-normal"> /mo</span></>}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {p.price_inr === 0 ? "Forever free during beta" : `≈ $${p.price_usd}/mo · ${p.monthly_coins.toLocaleString()} TeeBee Coins/mo`}
                </p>
              </div>

              <ul className="mt-5 space-y-2 flex-1">
                {p.benefits.slice(0, 8).map((b) => {
                  const BIcon = ICONS[b.icon] || Check;
                  return (
                    <li key={b.label} className="flex items-start gap-2 text-[13px]">
                      <BIcon className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                      <span>{b.label}</span>
                    </li>
                  );
                })}
              </ul>

              <Button
                onClick={() => select(p.tier)}
                disabled={isCurrent || selecting === p.tier}
                variant={p.cta_kind === "outline" ? "outline" : p.cta_kind === "sales" ? "outline" : "default"}
                className={`mt-6 w-full rounded-full ${isRecommended ? "" : ""}`}
                data-testid={`plan-select-${p.tier}`}
              >
                {selecting === p.tier ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : isCurrent ? (
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                ) : (
                  <ArrowRight className="h-3.5 w-3.5 mr-1.5" />
                )}
                {isCurrent ? "Current plan" : p.cta_label}
              </Button>
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground text-center">
        Real payments launching soon. Upgrade now to lock in beta pricing — plan switches are free during this period.
      </p>
    </section>
  );
}
